import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Eraser, FileSignature, Loader2, X } from "lucide-react";
import { adminClient } from "./api";

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio; c.height = 180 * ratio;
    ctx.scale(ratio, ratio);
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.offsetWidth, 180);
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#0C2E2B";
  }, []);

  const pos = (e) => { const r = ref.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); ref.current.setPointerCapture(e.pointerId); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); dirty.current = true; };
  const up = () => { if (!drawing.current) return; drawing.current = false; if (dirty.current) onChange(ref.current.toDataURL("image/png")); };
  const clear = () => { const c = ref.current; const ctx = c.getContext("2d"); ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height); ctx.restore(); dirty.current = false; onChange(""); };

  return (
    <div>
      <canvas ref={ref} data-testid="receipt-signature-canvas" className="w-full h-[180px] rounded-md border-2 border-dashed border-[#0C2E2B] bg-white touch-none cursor-crosshair"
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} onPointerCancel={up} />
      <div className="flex justify-between items-center mt-1">
        <span className="text-[10px] text-zinc-500">Customer signs above with a finger or stylus</span>
        <button type="button" data-testid="receipt-signature-clear-btn" onClick={clear} className="text-[11px] font-bold text-zinc-600 hover:text-[#0C2E2B] inline-flex items-center gap-1"><Eraser className="w-3 h-3" /> Clear</button>
      </div>
    </div>
  );
}

export default function DeliveryReceiptDialog({ job, open, onClose, onSaved }) {
  const [signedBy, setSignedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [sig, setSig] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [markComplete, setMarkComplete] = useState(true);
  const fileRef = useRef(null);

  useEffect(() => { if (open) { setSignedBy(job?.name || ""); setNotes(""); setSig(""); setPhotos([]); setMarkComplete(job?.status !== "completed"); } }, [open, job]);

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const f of Array.from(files || [])) {
        if (!/^image\//.test(f.type)) continue;
        const fd = new FormData(); fd.append("file", f);
        const { data } = await adminClient.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.url]);
      }
    } catch { toast.error("Photo upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!sig) return toast.error("Ask the customer to sign first");
    setSaving(true);
    try {
      const { data } = await adminClient.put(`/admin/bookings/${job.id}/receipt`, { signature_data_url: sig, photo_urls: photos, signed_by: signedBy, notes, send_email: sendEmail, site_url: window.location.origin });
      if (markComplete && job.status !== "completed") {
        await adminClient.put(`/admin/bookings/${job.id}/status`, { status: "completed", site_url: window.location.origin });
      }
      toast.success(`Receipt saved${data.email?.status === "sent" ? " · copy emailed to customer" : ""}${markComplete && job.status !== "completed" ? " · job marked complete" : ""}`, { duration: 6000 });
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save receipt"); } finally { setSaving(false); }
  };

  const abs = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-auto" data-testid="receipt-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-[#0C2E2B] flex items-center gap-2"><FileSignature className="w-5 h-5" /> Proof of delivery · {job?.ref_id}</DialogTitle>
          <DialogDescription>{job?.name} · {job?.address}. Snap the drop-off, get a signature, done.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Drop-off photos</div>
            <div className="flex flex-wrap gap-2">
              {photos.map((u, i) => (
                <div key={u} className="relative w-20 h-20 rounded-md overflow-hidden border-2 border-zinc-200" data-testid={`receipt-photo-${i}`}>
                  <img src={abs(u)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setPhotos((p) => p.filter((x) => x !== u))} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button type="button" data-testid="receipt-add-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-20 h-20 rounded-md border-2 border-dashed border-zinc-300 hover:border-[#0C2E2B] grid place-items-center text-zinc-500">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
              </button>
              <input ref={fileRef} data-testid="receipt-photo-input" type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Customer signature</div>
            <SignaturePad onChange={setSig} />
          </div>

          <Input data-testid="receipt-signed-by-input" placeholder="Name of person signing" value={signedBy} onChange={(e) => setSignedBy(e.target.value)} className="rounded-md" />
          <Textarea data-testid="receipt-notes-input" placeholder="Notes (e.g. left in garage, small scuff noted on arrival)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-md" />

          <div className="flex flex-col gap-1.5 text-xs font-bold text-zinc-700">
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="receipt-send-email-checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} disabled={!job?.email} className="w-4 h-4" /> Email a copy to the customer{!job?.email && " (no email on file)"}</label>
            {job?.status !== "completed" && <label className="flex items-center gap-2"><input type="checkbox" data-testid="receipt-mark-complete-checkbox" checked={markComplete} onChange={(e) => setMarkComplete(e.target.checked)} className="w-4 h-4" /> Also mark this job complete</label>}
          </div>

          <Button data-testid="receipt-save-btn" onClick={save} disabled={saving || uploading} className="w-full h-12 rounded-md bg-[#0C2E2B] text-[#FFDF00] hover:bg-[#0C2E2B]/90 font-black">
            {saving ? "Saving…" : "Save receipt"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
